
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import '../CSS/Components/ServicesCarousel.css'
// import { DotButton, useDotButton } from './EmblaCarouselDotButton'

function ServiceCarousel ({ slides, onLearnMore, onSelectedChange, nextRequestKey = 0, resetKey = 0, selectedServiceId = null }) {
  console.log('ServiceCarousel rendered with slides:', slides?.length || 0, slides)
  const mobileBreakpoint = 1024
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= mobileBreakpoint)

  const isLoop = slides && slides.length > 1;
  const emblaResult = useEmblaCarousel({
    loop: isLoop && !isMobile,
    align: isMobile ? 'start' : 'center',
    slidesToScroll: 1,
    containScroll: isMobile ? 'trimSnaps' : false,
    duration: 44,
    watchDrag: !isMobile,
    watchResize: false,
  })
  const emblaRef = emblaResult?.[0] ?? null;
  const emblaApi = emblaResult?.[1] ?? null;
  const [localSelectedIndex, setLocalSelectedIndex] = useState(0)
  const [servicesVisibleIndex, setServicesVisibleIndex] = useState(-1)
  const [activeServicePointIndexes, setActiveServicePointIndexes] = useState({})
  const [previousServicePointIndexes, setPreviousServicePointIndexes] = useState({})
  const activeServiceIndex = slides?.findIndex((service) => service.id === selectedServiceId) ?? -1
  const selectedIndex = activeServiceIndex >= 0 ? activeServiceIndex : localSelectedIndex

  useEffect(() => {
    if (!slides?.length) return

    if (activeServiceIndex >= 0) {
      setLocalSelectedIndex(activeServiceIndex)
    }
  }, [activeServiceIndex, slides])

  const servicesTimerRef = useRef(null)
  const lastHandledNextRequestRef = useRef(nextRequestKey)
  const heightFrameRefs = useRef([])
  const tweenNodes = useRef([])
  const titleRefs = useRef([]);
  const descRefs = useRef([]);
  const [descMaxWidths, setDescMaxWidths] = useState([]);
  const [collapsedWrapperWidths, setCollapsedWrapperWidths] = useState([]);
  const hasSlides = (slides?.length || 0) > 0
  const canSelectDifferentSlide = (slides?.length || 0) > 1

  const activateServicePoint = useCallback((slideIndex, serviceIndex) => {
    setActiveServicePointIndexes((currentIndexes) => {
      const currentIndex = currentIndexes[slideIndex] ?? 0
      if (currentIndex === serviceIndex) return currentIndexes

      setPreviousServicePointIndexes((previousIndexes) => ({
        ...previousIndexes,
        [slideIndex]: currentIndex,
      }))

      return { ...currentIndexes, [slideIndex]: serviceIndex }
    })
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint)
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const syncWrapperHeight = useCallback((wrapper, content, index) => {
    if (!wrapper || !content) return

    if (heightFrameRefs.current[index]) {
      cancelAnimationFrame(heightFrameRefs.current[index])
    }

    heightFrameRefs.current[index] = requestAnimationFrame(() => {
      const wrapperStyles = window.getComputedStyle(wrapper)
      const paddingTop = parseFloat(wrapperStyles.paddingTop) || 0
      const paddingBottom = parseFloat(wrapperStyles.paddingBottom) || 0
      const nextHeight = `${content.scrollHeight + paddingTop + paddingBottom}px`

      if (wrapper.style.height !== nextHeight) {
        wrapper.style.height = nextHeight
      }
    })
  }, [])

  const handleWrapperAction = useCallback((index) => {
    if (index === selectedIndex) return

    if (!emblaApi || !canSelectDifferentSlide) return

    setLocalSelectedIndex(index)
    if (typeof onSelectedChange === 'function') {
      onSelectedChange(slides[index], index)
    }
  }, [canSelectDifferentSlide, emblaApi, onSelectedChange, selectedIndex, slides])

  const handleWrapperClick = useCallback((index, event) => {
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('a, button, input, textarea, select, summary')
      if (interactiveElement && interactiveElement !== event.currentTarget) {
        return
      }
    }

    handleWrapperAction(index)
  }, [handleWrapperAction])

  const handleWrapperKeyDown = useCallback((index, event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    handleWrapperAction(index)
  }, [handleWrapperAction])

  // --- Delay services visibility slightly so it follows the slower slide expansion ---
  useEffect(() => {
    if (servicesTimerRef.current) clearTimeout(servicesTimerRef.current);

    if (isMobile) {
      setServicesVisibleIndex(selectedIndex);
      return undefined;
    }

    // Immediately hide services
    setServicesVisibleIndex(-1);
    // Show services for selected slide after the wrapper expansion has started
    servicesTimerRef.current = setTimeout(() => {
      setServicesVisibleIndex(selectedIndex);
    }, 440);
    return () => clearTimeout(servicesTimerRef.current);
  }, [isMobile, selectedIndex]);

  useEffect(() => {
    if (typeof onSelectedChange !== 'function') return;
    const currentSlide = slides?.[selectedIndex];
    if (!currentSlide) return;
    onSelectedChange(currentSlide, selectedIndex);
  }, [onSelectedChange, selectedIndex, slides]);

  useEffect(() => {
    if (!emblaApi) return
    if (!canSelectDifferentSlide) return
    if (nextRequestKey === lastHandledNextRequestRef.current) return

    lastHandledNextRequestRef.current = nextRequestKey

    if (isMobile) {
      const nextIndex = (selectedIndex + 1) % slides.length
      setLocalSelectedIndex(nextIndex)
      if (typeof onSelectedChange === 'function') {
        onSelectedChange(slides[nextIndex], nextIndex)
      }
      emblaApi.scrollTo(nextIndex)
      return
    }

    emblaApi.scrollNext()
  }, [canSelectDifferentSlide, emblaApi, isMobile, nextRequestKey, onSelectedChange, selectedIndex, slides])

  // --- Sync description maxWidth to title rendered width ---
  useEffect(() => {
    const updateWidths = () => {
      if (isMobile) {
        setDescMaxWidths([])
        setCollapsedWrapperWidths([])
        return
      }

      const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const collapsedWrapperOffset = rootFontSize * 7.5;
      const newWidths = slides.map((_, idx) => {
        const titleEl = titleRefs.current[idx];
        if (titleEl) {
          // scrollWidth gives the true rendered width of the title block
          return titleEl.scrollWidth;
        }
        return undefined;
      });
      setDescMaxWidths(newWidths);
      setCollapsedWrapperWidths(
        newWidths.map((width) => (width ? width + collapsedWrapperOffset : undefined))
      );
    };
    updateWidths();
    window.addEventListener('resize', updateWidths);
    // Also update after fonts load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateWidths);
    }
    return () => window.removeEventListener('resize', updateWidths);
  }, [isMobile, slides]);

  // --- Animate wrapper height smoothly using ResizeObserver ---
  useEffect(() => {
    tweenNodes.current.forEach((wrapper) => {
      if (wrapper) {
        wrapper.style.height = ''
      }
    })

    heightFrameRefs.current.forEach((frame) => {
      if (frame) cancelAnimationFrame(frame)
    })
    heightFrameRefs.current = []

    if (isMobile) return;
    if (!window.ResizeObserver) return;
    const observers = [];
    slides.forEach((_, idx) => {
      const wrapper = tweenNodes.current[idx];
      if (!wrapper) return;
      const content = wrapper.firstChild;
      if (!content) return;
      syncWrapperHeight(wrapper, content, idx)
      const ro = new window.ResizeObserver(() => {
        syncWrapperHeight(wrapper, content, idx)
      });
      ro.observe(content);
      observers.push(ro);
    });
    return () => {
      observers.forEach(ro => ro.disconnect())
      heightFrameRefs.current.forEach((frame) => {
        if (frame) cancelAnimationFrame(frame)
      })
      heightFrameRefs.current = []
    }
  }, [isMobile, selectedIndex, slides, syncWrapperHeight, tweenNodes]);



//   const { selectedIndex, scrollSnaps, onDotButtonClick } =
//     useDotButton(emblaApi)

//   const {
//     prevBtnDisabled,
//     nextBtnDisabled,
//     onPrevButtonClick,
//     onNextButtonClick
//   } = usePrevNextButtons(emblaApi)

  const setTweenNodes = useCallback((emblaApi) => {
    console.log('setTweenNodes called')
    const slideNodes = emblaApi.slideNodes()
    console.log('slideNodes found:', slideNodes?.length || 0, slideNodes)
    tweenNodes.current = slideNodes.map((slideNode, index) => {
      const wrapper = slideNode.querySelector('.embla__slide__wrapper')
      console.log('Slide', index, 'wrapper found:', !!wrapper, wrapper)
      return wrapper
    })
    console.log('tweenNodes set:', tweenNodes.current?.length || 0, 'nodes:', tweenNodes.current)
  }, [])



const tweenScale = useCallback((emblaApi, event) => {
  const scrollProgress = emblaApi.scrollProgress();
  const snapList = emblaApi.scrollSnapList();
  const slidesInView = emblaApi.slidesInView();
  const isScrollEvent = event?.type === 'scroll';
  const minScale = 0.92;
  const maxScale = 1;

  if (!tweenNodes.current) return;

  snapList.forEach((snap, snapIndex) => {
    let diffToTarget = snap - scrollProgress;
    if (isLoop) {
      diffToTarget = ((diffToTarget + 1.5) % 1) - 0.5;
    }

    const slideNode = tweenNodes.current[snapIndex];
    if (!slideNode) return;
    if (isScrollEvent && !slidesInView.includes(snapIndex)) return;

    const slide = slideNode.closest('.embla__slide');
    const isSelectedSlide = Boolean(slide && slide.classList.contains('is-selected'));

    if (isMobile || isSelectedSlide) {
      slideNode.style.transform = 'none';
      slideNode.style.willChange = 'auto';
      slideNode.style.height = '';
      return;
    }

    const absDiff = Math.abs(diffToTarget);
    const scale = minScale + (maxScale - minScale) * (1 - Math.min(absDiff, 1));
    slideNode.style.transform = `scale(${scale})`;
    slideNode.style.willChange = 'transform';
  });
}, [isLoop, isMobile]);

  const updateSelected = useCallback(() => {
    if (!emblaApi) return
    setLocalSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    console.log('useEffect triggered, emblaApi exists:', !!emblaApi)
    if (!emblaApi) return

    setTweenNodes(emblaApi)
    tweenScale(emblaApi)
    updateSelected()

    emblaApi
      .on('reinit', setTweenNodes)
      .on('reinit', tweenScale)
      .on('reinit', updateSelected)
      .on('scroll', tweenScale)
      .on('select', updateSelected)
      .on('slidefocus', tweenScale)

    requestAnimationFrame(() => {
      if (emblaApi) tweenScale(emblaApi)
    })

    setTimeout(() => {
      if (emblaApi) tweenScale(emblaApi)
    }, 100)

    console.log('Embla event listeners attached')

    return () => {
      emblaApi.off('reinit', setTweenNodes)
      emblaApi.off('reinit', tweenScale)
      emblaApi.off('reinit', updateSelected)
      emblaApi.off('scroll', tweenScale)
      emblaApi.off('select', updateSelected)
      emblaApi.off('slidefocus', tweenScale)
    }
  }, [emblaApi, setTweenNodes, tweenScale, updateSelected])

  useEffect(() => {
    if (!emblaApi || !isMobile) return undefined

    const frame = requestAnimationFrame(() => {
      emblaApi.reInit()
      tweenScale(emblaApi)
      updateSelected()
    })

    return () => {
      cancelAnimationFrame(frame)
    }
  }, [emblaApi, isMobile, slides.length, tweenScale, updateSelected])

  useEffect(() => {
    if (!emblaApi) return undefined

    const handleResize = () => {
      emblaApi.reInit()
      tweenScale(emblaApi)
      updateSelected()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [emblaApi, tweenScale, updateSelected])

  useEffect(() => {
    if (!emblaApi || !canSelectDifferentSlide) return

    const targetIndex = selectedIndex
    if (targetIndex < 0) return

    setServicesVisibleIndex(isMobile ? targetIndex : -1)

    const currentIndex = emblaApi.selectedScrollSnap()
    if (Math.abs(currentIndex - targetIndex) > 0.01) {
      requestAnimationFrame(() => {
        emblaApi.scrollTo(targetIndex)
        tweenScale(emblaApi)
        updateSelected()
      })
    }
  }, [canSelectDifferentSlide, emblaApi, isMobile, selectedIndex, tweenScale, updateSelected])

  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((service, index) => {
            console.log('Rendering slide:', index, 'service:', service.title)
            const isSelected = index === selectedIndex
            const isServicesVisible = isMobile || index === servicesVisibleIndex
            const collapsedWrapperWidth = collapsedWrapperWidths[index]
            const activeServicePointIndex = isSelected
              ? (activeServicePointIndexes[index] ?? 0)
              : null
            const activePoint = activeServicePointIndex !== null
              ? service.services[activeServicePointIndex]
              : null
            const previousServicePointIndex = previousServicePointIndexes[index]
            const previousPoint = previousServicePointIndex !== undefined
              ? service.services[previousServicePointIndex]
              : null
            return (
              <div
                className={`embla__slide ${isSelected ? 'is-selected' : ''}`}
                key={index}
                style={isMobile ? {
                  display: isSelected ? 'flex' : 'none',
                } : undefined}
              >
                <div
                  className="embla__slide__wrapper"
                  ref={el => tweenNodes.current[index] = el}
                  onClick={(event) => handleWrapperClick(index, event)}
                  onKeyDown={(event) => handleWrapperKeyDown(index, event)}
                  role={hasSlides ? 'button' : undefined}
                  tabIndex={hasSlides ? 0 : undefined}
                  aria-label={hasSlides ? (isSelected ? `Selected service: ${service.title}` : `Show ${service.title}`) : undefined}
                  style={{
                    width: isMobile ? '100%' : (!isSelected && collapsedWrapperWidth ? `${collapsedWrapperWidth}px` : '100%'),
                    maxWidth: '100%',
                  }}
                >
                  {/* Service Content: Description & Services */}
                  <div className="embla__slide__row">
                    {/* Left: Title + Description */}
                    <div className="embla__slide__main">
                      <div
                        className="embla__slide__title"
                        ref={el => titleRefs.current[index] = el}
                      >
                        {service.title.split(' ').map((word, i) => (
                          <span key={i}>{word}</span>
                        ))}
                      </div>
                      <div
                        className="embla__slide__description"
                        ref={el => descRefs.current[index] = el}
                        style={{
                          maxWidth: isMobile || isSelected ? '100%' : (descMaxWidths[index] ? descMaxWidths[index] + 'px' : '100%'),
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                      >
                        {previousPoint && previousServicePointIndex !== activeServicePointIndex && (
                          <div
                            key={`previous-${previousServicePointIndex}-${activeServicePointIndex}`}
                            className="embla__slide__description-copy embla__slide__description-copy--outgoing"
                          >
                            {previousPoint.description || service.desc}
                          </div>
                        )}
                        <div
                          key={`active-${activeServicePointIndex}`}
                          className="embla__slide__description-copy embla__slide__description-copy--incoming"
                        >
                          {activePoint?.description || service.desc}
                        </div>
                      </div>
                      {isSelected && (
                        <>
                          <button
                            type="button"
                            className="service-learn-more-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.currentTarget.blur();
                              onLearnMore?.(service, index);
                            }}
                          >
                            Learn more
                          </button>
                          <div className="service-carousel-dots" aria-label="Choose a service">
                            {slides.map((slide, slideIndex) => (
                              <button
                                type="button"
                                key={slide.id}
                                className={`service-carousel-dot ${slideIndex === selectedIndex ? 'is-active' : ''}`}
                                aria-label={`Show ${slide.title}`}
                                aria-current={slideIndex === selectedIndex ? 'true' : undefined}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  setLocalSelectedIndex(slideIndex)
                                  onSelectedChange?.(slide, slideIndex)
                                  emblaApi?.scrollTo(slideIndex)
                                }}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    {!isMobile && (
                      <div className="embla__slide__services" style={{
                        flexBasis: isSelected ? '50%' : '0%',
                      }}>
                        <div className="embla__slide__services-inner" style={{
                          opacity: isServicesVisible ? 1 : 0,
                          maxHeight: isSelected ? '1000px' : '0px',
                          padding: isSelected ? '8rem 0 4rem 6rem' : '0 0 0 0',
                        }}>
                          {service.services.map((svc, idx) => (
                            <button
                              type="button"
                              className={`embla__slide__service-item ${activeServicePointIndex === idx ? 'is-active' : ''}`}
                              key={svc.name}
                              onMouseEnter={() => activateServicePoint(index, idx)}
                              onFocus={() => activateServicePoint(index, idx)}
                            >
                              {svc.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
 
 
export default ServiceCarousel;
 